import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { DownOutlined } from 'components';
import { getColor } from 'utils';
import styled, { css, withTheme } from 'styled-components';
import constants from 'constants/index';
import _ from 'lodash';

const light = constants.THEME.LIGHT;

const SelectContainer = styled('div')`
  position: relative;
  width: 100%;
  min-height: 2.69625rem;
`;

const SelectButton = styled('button')`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 2rem 0.5rem 1rem;
  width: 100%;
  min-height: 2.69625rem;
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;
  color: ${props => props.$color || getColor(props, 'onPrimary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  font: inherit;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    background-color: ${props => getColor(props, 'lightGrey')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0.1rem 0.1rem ${props => getColor(props, 'accent')};
  }
`;

const ArrowContainer = styled('div')`
  position: absolute;
  top: 0.65rem;
  right: 0.5rem;
  pointer-events: none;
`;

const FilterContainer = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const OptionsContainer = styled('div')`
  position: fixed;
  ${props => props.$position === 'top'
    ? css`bottom: ${props.$bottom}px;`
    : css`top: ${props.$top}px;`
  }
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  z-index: 9999;
  background-color: ${props => {
    const lightTheme = props.$selectedTheme === light;
    return getColor(props, lightTheme ? 'white' : 'darkGrey');
  }};
  box-shadow: 0 0.1rem 0.25rem ${props => {
    const lightTheme = props.$selectedTheme === light;
    return getColor(props, lightTheme ? 'darkGrey' : 'black');
  }};
  max-height: 16.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: ${props => {
    if (props.$position === 'top') {
      return '0.25rem 0.25rem 0 0';
    }

    return '0 0 0.25rem 0.25rem';
  }};
`;

const OptionButton = styled('button')`
  display: block;
  width: 100%;
  padding: 0.35rem 1rem;
  border: 0;
  background-color: ${props => {
    const lightTheme = props.$selectedTheme === light;
    return getColor(props, lightTheme ? 'white' : 'darkGrey');
  }};
  color: ${props => getColor(props, 'onPrimary')};
  text-align: left;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;

  &:hover,
  &:focus-visible {
    outline: none;
    background-color: ${props => {
      const lightTheme = props.$selectedTheme === light;
      return getColor(props, lightTheme ? 'lightGrey' : 'mediumGrey');
    }};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const FilterInput = styled('input')`
  padding: 0.5rem 2rem 0.5rem 1rem;
  width: 100%;
  min-height: 2.5rem;
  min-width: 3.25rem;
  border-radius: ${props => {
    if (props.$position === 'top') {
      return '0 0 0.25rem 0.25rem';
    }

    return '0.25rem 0.25rem 0 0';
  }};
  background: transparent;
  color: ${props => getColor(props, 'onPrimary')};
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};

  &:focus-visible {
    outline: none;
  }
`;

const getOptionLabel = option => {
  const optionChildren = option?.props?.children;

  if (_.isString(optionChildren) || _.isNumber(optionChildren)) {
    return String(optionChildren);
  }

  return String(option?.props?.value || '');
};

const FovSelect = withTheme(props => {
  const { theme, children, placeholder, position, color, onOpen, ...rest } = props;
  const [ opened, setOpened ] = useState(false);
  const [ inputVal, setInputVal ] = useState('');
  const userDataStore = useSelector(state => state.userData);
  const selectedTheme = userDataStore.userPreferences.theme;
  const containerRef = useRef();
  const buttonRef = useRef();
  const filterRef = useRef();
  const [ fixedPos, setFixedPos ] = useState({ top: 0, bottom: 0, left: 0, width: 0 });

  const options = useMemo(() => {
    return _.flattenDeep(React.Children.toArray(children))
      .filter(option => !_.isNil(option?.props?.value) || !_.isNil(option?.props?.children));
  }, [ children ]);

  const filteredOptions = useMemo(() => {
    const input = inputVal.trim().toLowerCase();

    if (_.isEmpty(input)) {
      return options;
    }

    return options.filter(option => {
      const label = getOptionLabel(option).toLowerCase();
      const value = String(option.props?.value || '').toLowerCase();

      return label.includes(input) || value.includes(input);
    });
  }, [ inputVal, options ]);

  const selectedOption = useMemo(() => {
    return options.find(option => String(option.props?.value) === String(rest.value));
  }, [ options, rest.value ]);

  const selectedLabel = getOptionLabel(selectedOption) || placeholder || '';

  const updateFixedPosition = useCallback(() => {
    const anchor = buttonRef.current || filterRef.current || containerRef.current;

    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();

    setFixedPos({
      top: rect.bottom,
      bottom: window.innerHeight - rect.top,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  const closeOptions = useCallback(() => {
    setInputVal('');
    setOpened(false);
  }, []);

  const openOptions = useCallback(() => {
    if (rest.disabled) {
      return;
    }

    updateFixedPosition();
    setOpened(true);
    onOpen?.();
  }, [ onOpen, rest.disabled, updateFixedPosition ]);

  const handleSelect = (event, value) => {
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        name: rest.name,
        value,
      },
      currentTarget: {
        ...event.currentTarget,
        name: rest.name,
        value,
      },
    };

    rest.onChange?.(syntheticEvent, value);
    closeOptions();
  };

  useEffect(() => {
    if (!opened) {
      return undefined;
    }

    updateFixedPosition();

    const canHover = window.matchMedia?.('(hover: hover)').matches;

    if (canHover) {
      filterRef.current?.focus();
    }

    const handleOutsideClick = event => {
      if (!containerRef.current?.contains(event.target)) {
        closeOptions();
      }
    };

    window.addEventListener('resize', updateFixedPosition);
    window.addEventListener('scroll', updateFixedPosition, true);
    document.addEventListener('pointerdown', handleOutsideClick);

    return () => {
      window.removeEventListener('resize', updateFixedPosition);
      window.removeEventListener('scroll', updateFixedPosition, true);
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [ closeOptions, opened, updateFixedPosition ]);

  const handleInputKeyDown = event => {
    if (event.key === 'Escape') {
      closeOptions();
      buttonRef.current?.focus();
      return;
    }

    if (event.key === 'Enter' && filteredOptions.length === 1) {
      handleSelect(event, filteredOptions[0].props.value);
    }
  };

  return (
    <SelectContainer ref={containerRef}>
      <FilterContainer>
        {opened && (
          <>
            <FilterInput
              name={`filterInput${rest.name || 'select'}`}
              theme={theme}
              $selectedTheme={selectedTheme}
              $position={position}
              ref={filterRef}
              type='text'
              value={inputVal}
              placeholder={placeholder || 'Filter options'}
              onChange={e => setInputVal(e.currentTarget.value)}
              onKeyDown={handleInputKeyDown}
            />

            <OptionsContainer
              theme={theme}
              $selectedTheme={selectedTheme}
              $position={position}
              $top={fixedPos.top}
              $bottom={fixedPos.bottom}
              $left={fixedPos.left}
              $width={fixedPos.width}
            >
              {filteredOptions.map((option, index) => (
                <OptionButton
                  key={option.key || `${option.props.value}-${index}`}
                  type='button'
                  theme={theme}
                  $selectedTheme={selectedTheme}
                  disabled={option.props.disabled}
                  onClick={event => handleSelect(event, option.props.value)}
                >
                  {option.props.children || option.props.value}
                </OptionButton>
              ))}
            </OptionsContainer>
          </>
        )}

        {!opened && (
          <SelectButton
            ref={buttonRef}
            type='button'
            theme={theme}
            $selectedTheme={selectedTheme}
            disabled={rest.disabled}
            onClick={openOptions}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openOptions();
              }
            }}
            $color={color}
          >
            {selectedLabel}
          </SelectButton>
        )}
      </FilterContainer>

      <ArrowContainer>
        <DownOutlined width={16} height={16} color={color} />
      </ArrowContainer>
    </SelectContainer>
  );
});

export { FovSelect };
